import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";
import { MemberSearchApi } from "@/api/giftcard.api";
import { OTP_LENGTH } from "../mockData";
import {
  mapVerifiedMemberDetails,
  type MappedVerifiedMember,
} from "../mapVerifiedMember";

interface OTPVerificationProps {
  onVerified: (mapped: MappedVerifiedMember) => void;
}

const emptyOtp = () => Array(OTP_LENGTH).fill("");

function getApiErrorMessage(err: unknown, fallback: string) {
  if (!axios.isAxiosError(err)) return fallback;
  const data = err.response?.data as
    | { error_msg?: string; message?: string }
    | undefined;
  return data?.error_msg || data?.message || fallback;
}

export function OTPVerification({ onVerified }: OTPVerificationProps) {
  const [step, setStep] = useState<"mobile" | "otp">("mobile");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState<string[]>(emptyOtp);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setTimeout(() => setResendSeconds((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendSeconds]);

  const searchMember = async (digits: string) => {
    const data = await MemberSearchApi.memberSearch({
      p_MobileNo: digits,
    });
    if (data?.error_msg) {
      throw new Error(data.error_msg);
    }
    return data;
  };

  const handleSendOtp = async () => {
    setError("");
    const digits = mobile.replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setIsSending(true);
    try {
      await searchMember(digits);
      setStep("otp");
      setResendSeconds(30);
      setOtp(emptyOtp());
      window.setTimeout(() => inputRefs.current[0]?.focus(), 80);
    } catch (err) {
      console.error("MemberSearch failed:", err);
      if (axios.isAxiosError(err)) {
        setError(
          getApiErrorMessage(
            err,
            "Unable to verify membership. Please try again.",
          ),
        );
      } else if (err instanceof Error && err.message) {
        setError(err.message);
      } else {
        setError("Unable to verify membership. Please try again.");
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setError("");
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = emptyOtp();
    pasted.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerifyOtp = async () => {
    setError("");
    const code = otp.join("");
    const digits = mobile.replace(/\D/g, "");

    if (code.length !== OTP_LENGTH) {
      setError(`Please enter the ${OTP_LENGTH}-digit OTP.`);
      return;
    }
    if (digits.length < 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setIsVerifying(true);
    try {
      const data = await MemberSearchApi.verifyOtp({
        p_MobileNo: digits,
        p_OTP: code,
      });

      if (data?.error_msg) {
        setError(data.error_msg);
        return;
      }

      const mapped = mapVerifiedMemberDetails(data?.memberDetails?.[0], digits);
      onVerified(mapped);
    } catch (err) {
      console.error("VerifyOTP failed:", err);
      if (axios.isAxiosError(err)) {
        setError(getApiErrorMessage(err, "Invalid OTP. Please try again."));
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendSeconds > 0 || isSending) return;
    setError("");
    const digits = mobile.replace(/\D/g, "");
    if (digits.length < 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    setIsSending(true);
    try {
      await searchMember(digits);
      setOtp(emptyOtp());
      setResendSeconds(30);
      inputRefs.current[0]?.focus();
    } catch (err) {
      console.error("MemberSearch resend failed:", err);
      if (axios.isAxiosError(err)) {
        setError(
          getApiErrorMessage(err, "Unable to resend OTP. Please try again."),
        );
      } else if (err instanceof Error && err.message) {
        setError(err.message);
      } else {
        setError("Unable to resend OTP. Please try again.");
      }
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.25)] sm:p-10">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-amber-100/60 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-12 h-40 w-40 rounded-full bg-sky-100/50 blur-2xl" />

          <div className="relative">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/25">
              {step === "mobile" ? (
                <Smartphone className="h-8 w-8 text-white" />
              ) : (
                <ShieldCheck className="h-8 w-8 text-white" />
              )}
            </div>

            <AnimatePresence mode="wait">
              {step === "mobile" ? (
                <motion.div
                  key="mobile"
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.25 }}
                >
                  <h1 className="text-center text-2xl font-semibold tracking-tight text-slate-900">
                    Verify Your Membership
                  </h1>
                  <p className="mt-2 text-center text-sm leading-relaxed text-slate-500">
                    Enter your registered mobile number to securely access your
                    loyalty account.
                  </p>

                  <label className="mt-8 block text-sm font-medium text-slate-700">
                    Mobile Number
                  </label>
                  <div className="mt-2 flex gap-2">
                    <div className="flex items-center rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-600">
                      +91
                    </div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={mobile}
                      onChange={(e) =>
                        setMobile(
                          e.target.value.replace(/\D/g, "").slice(0, 10),
                        )
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void handleSendOtp();
                      }}
                      placeholder="Enter Mobile Number"
                      className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100"
                    />
                  </div>

                  {error ? (
                    <p className="mt-3 text-sm text-rose-600">{error}</p>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => void handleSendOtp()}
                    disabled={isSending}
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/15 transition hover:bg-slate-800 disabled:opacity-70"
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                </motion.div>
              ) : (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  <h1 className="text-center text-2xl font-semibold tracking-tight text-slate-900">
                    Verify OTP
                  </h1>
                  <p className="mt-2 text-center text-sm leading-relaxed text-slate-500">
                    We&apos;ve sent a 4-digit verification code
                    {mobile ? ` to +91 ${mobile}` : ""}.
                  </p>

                  <div
                    className="mt-8 flex justify-center gap-2 sm:gap-3"
                    onPaste={handleOtpPaste}
                  >
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          inputRefs.current[index] = el;
                        }}
                        type="password"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="h-12 w-10 rounded-xl border border-slate-200 bg-white text-center text-lg font-semibold text-slate-900 outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100 sm:h-14 sm:w-12"
                        aria-label={`OTP digit ${index + 1}`}
                      />
                    ))}
                  </div>

                  {error ? (
                    <p className="mt-3 text-center text-sm text-rose-600">
                      {error}
                    </p>
                  ) : null}

                  <button
                    type="button"
                    onClick={() => void handleVerifyOtp()}
                    disabled={isVerifying}
                    className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/15 transition hover:bg-slate-800 disabled:opacity-70"
                  >
                    {isVerifying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      "Verify OTP"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => void handleResend()}
                    disabled={resendSeconds > 0 || isSending}
                    className="mt-3 w-full rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-400"
                  >
                    {isSending
                      ? "Sending…"
                      : resendSeconds > 0
                        ? `Resend OTP in ${resendSeconds}s`
                        : "Resend OTP"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep("mobile");
                      setError("");
                      setOtp(emptyOtp());
                    }}
                    className="mt-1 w-full text-center text-xs text-slate-400 transition hover:text-slate-600"
                  >
                    Change mobile number
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
